<template>
  <AddOnError
    v-if="showLoadingError"
    errorTitle="ADD_ONS.ERROR_LOADING"
    errorDescription="ADD_ONS.ERROR_LOADING_DESCRIPTION"
    :deviceId="deviceTag"
    :onTryAgain="retryLoadAddOn"
    :onClose="goToHome"
  />
  <AddOnError
    v-else-if="showActivationError"
    errorTitle="ADD_ONS.ERROR_ACTIVATION"
    errorDescription="ADD_ONS.ERROR_ACTIVATION_DESCRIPTION"
    :deviceId="deviceTag"
    :onTryAgain="retryActivateAddOn"
    :onClose="hideActivationError"
  />
  <AddOnPending
    v-else-if="isActivationPending"
    :addOnDetails="addOnDetails"
    :onClose="goToHome"
  />
  <AddOnConfirmation
    v-else-if="isActivationComplete"
    :addOnDetails="addOnDetails"
    :onClose="goToHome"
  />
  <div v-else>
    <div class="d-flex flex-column justify-content-between h-100">
      <AddOnActivationForm
        :addOnDetails="addOnDetails"
        :isActivating="isActivating"
        :onActivateAddOn="activateAddOn"
        :onClose="goToHome"
      />
    </div>
  </div>
  <LoadingOverlay :loading="isLoading" />
</template>

<script setup lang="ts">
import AddOnActivationForm from "@/apps/credit-line/components/AddOnActivationForm.vue";
import AddOnConfirmation from "@/apps/credit-line/components/AddOnConfirmation.vue";
import AddOnPending from "@/apps/credit-line/components/AddOnPending.vue";
import type { AxiosError } from "axios";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { onMounted, ref } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import type { AndroidInterface } from "@/apps/credit-line/services/android.service";
import type { ActivateCartAddOnRequest } from "@/apps/credit-line/types/requests";
import type { IAddOnDetailsResponse, IActivateCartAddOnResponse } from "@/apps/credit-line/types/responses";
import AddOnError from "@/apps/credit-line/components/AddOnError.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";

const apiService = new Service();
const addOnDetails = ref<IAddOnDetailsResponse | null>(null);
const isActivating = ref(false);
const isLoading = ref(true);
const isActivationComplete = ref(false);
const isActivationPending = ref(false);
const showLoadingError = ref(false);
const showActivationError = ref(false);
const deviceTag = ref<string>("");

declare const Android: AndroidInterface;

onMounted(async () => {
  deviceTag.value = AuthenticationHelper.getDeviceTag();
  await loadAddOnDetails();
  Android.finishedLoading();

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AddOnActivationPageDisplayed,
    "AddOns",
    undefined
  );
});

const loadAddOnDetails = async () => {
  try {
    isLoading.value = true;
    showLoadingError.value = false;
    const response = await apiService.getCartAddOn({
      deviceTag: deviceTag.value,
    });
    addOnDetails.value = response?.data;
  } catch (error) {
    showLoadingError.value = true;
    const axiosError = error as AxiosError;
    const dataErrorResponse = axiosError.response?.data;
    const errorLog =
      typeof dataErrorResponse === "string"
        ? dataErrorResponse
        : JSON.stringify(dataErrorResponse);

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.AddOnLoadingError,
      "AddOns",
      undefined,
      {
        errorMessage: errorLog,
      }
    );
    addOnDetails.value = null;
  } finally {
    isLoading.value = false;
  }
};

const activateAddOn = async () => {
  if (!addOnDetails.value || isActivating.value) return;

  isActivating.value = true;
  showActivationError.value = false;

  const deviceTagValue = AuthenticationHelper.getDeviceTag();
  const payload: ActivateCartAddOnRequest = {
    deviceTag: deviceTagValue,
    cartAddOnId: addOnDetails.value.cartAddOnId,
  };

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AddOnActivateServiceClicked,
    "AddOns",
    undefined,
    {
      cartAddOnId: addOnDetails.value.cartAddOnId,
      addOnName: addOnDetails.value.addOnName,
    }
  );

  try {
    const response = await apiService.activateCartAddOn(payload);
    const activationData = response?.data as IActivateCartAddOnResponse;
    if (activationData?.activationStatus === "pending") {
      isActivationPending.value = true;
    } else {
      isActivationComplete.value = true;
    }
  } catch (error) {
    showActivationError.value = true;
    const axiosError = error as AxiosError;
    const dataErrorResponse = axiosError.response?.data;
    const errorLog =
      typeof dataErrorResponse === "string"
        ? dataErrorResponse
        : JSON.stringify(dataErrorResponse);

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.AddOnActivationError,
      "AddOns",
      undefined,
      {
        errorMessage: errorLog,
      }
    );
    showActivationError.value = true;
  } finally {
    isActivating.value = false;
  }
};

const retryLoadAddOn = () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AddOnLoadingRetryClicked,
    "AddOns"
  );
  loadAddOnDetails();
};

const retryActivateAddOn = () => {
  showActivationError.value = false;

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AddOnActivationRetryClicked,
    "AddOns",
    undefined,
    {
      cartAddOnId: addOnDetails.value?.cartAddOnId,
      addOnName: addOnDetails.value?.addOnName,
    }
  );

  activateAddOn();
};

const hideActivationError = () => {
  showActivationError.value = false;
};

const goToHome = () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AddOnCloseWindowClicked,
    "AddOns"
  );

  Android.dismissWebView();
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
@import "@/assets/scss/_spacing-utils.scss";

.nav-header {
  border-bottom: 1px solid #eee;
}

.add_on-activation-title {
  font-size: 1rem;
  font-weight: 500;
  font-family: Inter;
  line-height: 1.5rem;
  margin-bottom: 0;
  color: #111;
}
.close-view {
  color: #1c1b1f;
}
</style>
